import type { AlignmentPolicy, IndicatorSeries, Layer, Observation, Unit } from "../types";

interface DeriveOptions {
  id: string;
  name: string;
  shortName: string;
  a: IndicatorSeries;
  b: IndicatorSeries;
  unit?: Unit;
  layer?: Layer;
  alignment?: AlignmentPolicy;
  scale?: number;
  decimals?: number;
  note?: string;
}

function latestAtOrBefore(observations: Observation[], date: string): number | null {
  let lo = 0;
  let hi = observations.length - 1;
  let answer: number | null = null;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const obs = observations[mid];
    if (obs.date <= date) {
      if (obs.value != null) answer = obs.value;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return answer;
}

function qualityFor(a: IndicatorSeries, b: IndicatorSeries) {
  if (a.source.quality === "unavailable" || b.source.quality === "unavailable") return "unavailable" as const;
  if (a.source.quality === "stale" || b.source.quality === "stale") return "stale" as const;
  if (a.source.quality === "demo" || b.source.quality === "demo") return "demo" as const;
  return "official" as const;
}

export function deriveDifference({
  id,
  name,
  shortName,
  a,
  b,
  unit = "bp",
  layer = "market",
  alignment = "exact",
  scale = unit === "bp" ? 100 : 1,
  decimals = unit === "bp" ? 1 : 3,
  note
}: DeriveOptions): IndicatorSeries {
  const aSorted = [...a.observations].sort((x, y) => x.date.localeCompare(y.date));
  const bMap = new Map(b.observations.map((o) => [o.date, o.value]));
  const bSorted = [...b.observations].sort((x, y) => x.date.localeCompare(y.date));
  const baseDates = alignment === "step_hold_union"
    ? Array.from(new Set([...aSorted.map((o) => o.date), ...bSorted.map((o) => o.date)])).sort()
    : aSorted.map((o) => o.date);
  const aMap = new Map(aSorted.map((o) => [o.date, o.value]));
  const observations = baseDates.map((date) => {
    const av = alignment === "step_hold_union" ? latestAtOrBefore(aSorted, date) : (aMap.get(date) ?? null);
    const bv = alignment === "step_hold" || alignment === "step_hold_union"
      ? latestAtOrBefore(bSorted, date)
      : (bMap.get(date) ?? null);
    if (av == null || bv == null) return { date, value: null };
    return {
      date,
      value: Number(((av - bv) * scale).toFixed(decimals))
    };
  });

  const observationDate = [...observations].reverse().find((o) => o.value != null)?.date;

  return {
    id,
    name,
    shortName,
    unit,
    layer,
    source: {
      provider: "Calculated",
      dataset: `${a.shortName} - ${b.shortName}`,
      url: "",
      frequency: a.source.frequency,
      quality: qualityFor(a, b),
      observationDate,
      collectedAt: [a.source.collectedAt, b.source.collectedAt].filter(Boolean).sort().at(-1),
      note: [
        alignment === "step_hold"
          ? `Alignment: ${a.shortName} dates with last effective ${b.shortName} observation (policy step-hold; not numeric interpolation).`
          : alignment === "step_hold_union"
            ? `Alignment: union of ${a.shortName}/${b.shortName} policy observation dates with each last effective policy setting held until the next change (step function; not numeric interpolation).`
            : "Alignment: exact matching observation dates only; missing values remain N/A.",
        note
      ].filter(Boolean).join(" "),
      sourceIds: [a.id, b.id]
    },
    observations
  };
}

export function unavailableSeries(
  id: string,
  name: string,
  shortName: string,
  layer: Layer,
  frequency: IndicatorSeries["source"]["frequency"] = "monthly",
  unit: Unit = "%",
  note = "공식 원자료를 불러오지 못해 N/A로 표시합니다."
): IndicatorSeries {
  return {
    id,
    name,
    shortName,
    unit,
    layer,
    source: {
      provider: "N/A",
      dataset: "Unavailable",
      url: "",
      frequency,
      quality: "unavailable",
      note
    },
    observations: []
  };
}
